import { COUNTRY_MAP, normalizeCountryCode } from "./Countries";
import { US_STATE_MAP, CANADA_PROVINCE_MAP } from "./States";
import { formatSectorLabel } from "../constants/sectors";

/* =========================
   Cleaning / Formatting
========================= */

function looksLikeGarbage(s = "") {
  const low = String(s).trim().toLowerCase();
  if (!low) return true;
  if (low.startsWith("translation missing:")) return true;
  if (low.startsWith("en.")) return true;
  if (low.includes("unknown_")) return true;
  return false;
}

export function cleanDisplay(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  return looksLikeGarbage(s) ? "" : s;
}

export function displayOrDash(v) {
  const s = cleanDisplay(v);
  return s || "- - - -";
}

/* =========================
   Location Helpers
========================= */

export function getStateDisplay(value, country) {
  const cleaned = cleanDisplay(value);
  if (!cleaned) return "";

  const normalizedCountry = normalizeCountryCode(cleanDisplay(country));
  const normalizedState = cleaned.toUpperCase();

  if (normalizedCountry === "CA" && CANADA_PROVINCE_MAP[normalizedState]) {
    return CANADA_PROVINCE_MAP[normalizedState];
  }

  if (normalizedState.length === 2 && US_STATE_MAP[normalizedState]) {
    return US_STATE_MAP[normalizedState];
  }

  return cleaned;
}

export function deriveCountry(row) {
  const rawCountry = cleanDisplay(row.visitor_country);
  const rawState = cleanDisplay(row.visitor_state);

  if (rawCountry) {
    const normalized = normalizeCountryCode(rawCountry);
    return COUNTRY_MAP[normalized] || rawCountry;
  }

  if (rawState && /^[A-Z]{2}$/.test(rawState)) {
    return "United States";
  }

  return "";
}

export function normalizeLocationForBRE({ city, state, country }) {
  const s = cleanDisplay(state);
  const c = cleanDisplay(country);

  if (c && /^[A-Z]{2}$/.test(c) && s && s.length > 2) {
    return { city, state: c };
  }

  return { city, state: s };
}

/* =========================
   NAICS / Sector
========================= */

export function getSector(row = {}) {
  const rawSector =
    cleanDisplay(row.sector) ||
    cleanDisplay(row?.naics_extra?.new_sector) ||
    cleanDisplay(row?.naics_extra?.sector) ||
    "";

  return formatSectorLabel(rawSector);
}

export function getNaicsFields(row = {}) {
  const extra = row?.naics_extra || {};

  const possibleNames = [
    row.naics_name,
    row.primary_naics_bucket,
    row.naics_title,
    row.industry,
    row.industry_name,
    row.industry_title,
    extra.new_title,
    extra.title,
    extra.name,
    extra.industry,
    extra.industry_name,
    extra.naics_name,
  ];

  const possibleCodes = [
    row.naics_code,
    row.naics,
    extra.new_naicsprefixcode,
    extra.new_code,
    extra.new_naicscode,
    extra.code,
    extra.naics,
    extra.naics_code,
  ];

  const naicsName = possibleNames.map(cleanDisplay).find(Boolean) || "";
  const naicsCode = possibleCodes.map(cleanDisplay).find(Boolean) || "";

  return { naicsName, naicsCode };
}

/* =========================
   IDs / Keys
========================= */

export function normalizeGuid(s = "") {
  return String(s).trim().replace(/[{}]/g, "");
}

export function getVisitCompanyKey(row = {}) {
  const acct =
    cleanDisplay(row.visitor_account_id) || cleanDisplay(row.account_id);

  if (acct) return `id:${acct}`;

  const name =
    cleanDisplay(row.visitor_company_name) ||
    cleanDisplay(row.visitor_account_name);
  const state = cleanDisplay(row.visitor_state);

  if (name && state)
    return `name_state:${name.toLowerCase()}|${state.toLowerCase()}`;
  if (name) return `name:${name.toLowerCase()}`;

  return null;
}

export function getGroupId(group) {
  return group.companyKey || null;
}
